import * as React from "react"

import { cn } from "../../cn"
import { CopyableId } from "../CopyableId/CopyableId"
import { normalizeResourceCopyTargets } from "../ResourceMenu/ResourceMenu"
import type { ColumnDef, RowCopyTargets } from "./types"

export const DEFAULT_COLUMN_WIDTH = 150
/**
 * Default width (px) of a `variant: "id"` column: a 4-character `CopyableId`
 * plus cell padding, so a pinned ID column sits as close to its divider as to
 * the table edge.
 */
export const ID_COLUMN_WIDTH = 64
/** Narrowest (px) a user can drag a column that declares no `minWidth`. */
export const MIN_RESIZE_WIDTH = 48
/** Widest (px) a user can drag a column, unless its declared width is wider. */
export const MAX_RESIZE_WIDTH = 1200
export const SKELETON_ROW_COUNT = 8
/** Estimated row height for the virtualizer (extracted from VirtualRunsTable). */
export const ESTIMATED_ROW_HEIGHT = 52
/** Render this many rows beyond the viewport on each side. */
export const OVERSCAN = 100
/** Fetch the next page once the viewport is within this many rows of the end. */
export const LOAD_MORE_ROW_THRESHOLD = 15
/**
 * Width (px) of the leading checkbox column's checkbox slot when `selectable`.
 * `Table` adds the edge gutter to it, and the cell pads its trailing side by the
 * same gutter, so the checkbox sits as far from the pinned-column divider as
 * from the table edge.
 *
 * `Checkbox` is `size-4`, which is 14.4px under `html { font-size: 0.9rem }`;
 * the 16px here is the nearest whole px that can't clip it, and the cell
 * centers it in that slot.
 */
export const SELECT_COLUMN_WIDTH = 16
/**
 * The `cardRows` counterpart. A platter's cells keep their own padding (see
 * `FLUSH_EDGE_CELLS_CLASS`), so the checkbox sits one `pl-3` in from the platter
 * edge and the slot has to carry that inset as well as the checkbox.
 */
export const SELECT_COLUMN_WIDTH_CARD = 26
/**
 * Fixed width (px) of the trailing row-actions column when `rowActions` is set.
 * With the 12px edge gutter beside it, the centered trigger gets the same
 * horizontal inset as its vertical inset in a standard 52px row.
 */
export const ACTION_COLUMN_WIDTH = 40
/** Plain-text name for a column: `headerLabel`, else a string `header`, else `id`. */
export function columnLabel<T>(column: ColumnDef<T>): string {
  return (
    column.headerLabel ??
    (typeof column.header === "string" ? column.header : column.id)
  )
}

/**
 * The column whose cell identifies a row when no `getRowLabel` is given: the
 * `variant: "name"` column, else the first non-id column. Row controls take
 * that cell's rendered text into their names, so a cell-only column that
 * derives its text from other fields still labels the row.
 */
export function rowLabelColumnId<T>(
  columns: (ColumnDef<T> | undefined)[],
): string | undefined {
  const candidates = columns.filter(
    (col): col is ColumnDef<T> => col != null && col.variant !== "id",
  )
  return (candidates.find((col) => col.variant === "name") ?? candidates[0])?.id
}

/**
 * The words each control that repeats on every row leads its name with.
 * `Table` renders one hidden copy of each, outside the rows, for the controls'
 * `aria-labelledby` to point at, so the words never land in a row's text.
 */
export const ROW_CONTROL_WORDS = {
  select: "Select row",
  expand: "Expand row",
  collapse: "Collapse row",
  actions: "Row actions",
} as const

/**
 * Name props for a control that repeats on every row ("Select row"), carrying
 * the row it acts on: the `rowLabel` text, else the rendered text of the row's
 * label cell through `aria-labelledby`, pointing at `Table`'s hidden copy of
 * the control's words (`namesId`).
 */
export function rowControlName({
  kind,
  namesId,
  rowLabel,
  rowLabelCellId,
}: {
  kind: keyof typeof ROW_CONTROL_WORDS
  namesId: string | undefined
  rowLabel: string | undefined
  rowLabelCellId: string | undefined
}) {
  const words = ROW_CONTROL_WORDS[kind]
  if (rowLabel) {
    return { "aria-label": `${words}: ${rowLabel}` }
  }
  if (rowLabelCellId && namesId) {
    return { "aria-labelledby": `${namesId}-${kind} ${rowLabelCellId}` }
  }
  return { "aria-label": words }
}

/** Left indent (px) per nesting depth for expandable rows. */
export const NEST_INDENT = 16
/**
 * Outer gutter on the table's left/right edges — the table's single horizontal
 * inset, matching the vertical padding of the controls row above it so a
 * table's content sits as far from the left edge as it does from the top.
 * Applied as horizontal padding on each row's grid container, while the row
 * dividers stay full-bleed (the border wraps the padded box; only the grid
 * tracks inset). Sticky offsets add `EDGE_GUTTER_PX` so pinned / selection /
 * action columns stay glued to the gutter during horizontal scroll.
 *
 * Uses an explicit px value (not `px-3`) so it stays exactly `EDGE_GUTTER_PX`
 * real pixels. The table's geometry is raw px (grid tracks + sticky offsets),
 * but the app sets `html { font-size: 0.9rem }`, under which rem-based spacing
 * (`px-3` = 0.75rem ≈ 10.8px) would fall ~1px short of the 12px offsets — the
 * pinned/selection cells would then sit just inside the gutter cover and let
 * scrolling content peek through at the edge.
 *
 * The width is read from the `--table-edge-gutter` CSS variable (set on the
 * root, defaulting to `EDGE_GUTTER_PX`) so `edgeToEdge` can collapse it to zero
 * for tables embedded in an already-padded container. Sticky-column offsets
 * can't reference the variable (they're numeric inline styles asserted in px by
 * tests), so those thread the resolved `edgeGutterPx` value instead.
 */
export const EDGE_GUTTER_PX = 12

/**
 * The gutter an `edgeToEdge` table keeps: the 2px a focus ring draws past a
 * control on the table's edge (1px offset + 1px ring).
 */
export const EDGE_TO_EDGE_RING_ROOM_PX = 2
// The `table-pad` / `table-pad-x` utilities in `globals.css` pad a table's own
// chrome by this same amount, so a controls row's search box lines up with the
// first column and sits as far from the left edge as from the top. Changing one
// means changing the other.
// Reads `--table-edge-gutter` (set on the root) with a 12px fallback. The var
// reference is a literal so Tailwind's JIT scanner still emits the class.
export const EDGE_GUTTER_CLASS = "px-[var(--table-edge-gutter,12px)]"
// Keeps the gutter the table's only horizontal inset: every cell carries its own
// `px-3`, which would stack on the gutter and push the first / last column twice
// as far in as the row's top padding. Drop just the outer cell's leading /
// trailing padding (the row grid's first / last child) so the first column's
// content starts exactly at the gutter; inter-column padding between cells is
// untouched, and under `edgeToEdge` — where the gutter itself is zero — the
// columns land flush with the edge. Skipped in `cardRows` mode: platters keep
// their cell padding so text stays inset from the platter border.
export const FLUSH_EDGE_CELLS_CLASS =
  "[&>*:first-child]:pl-0 [&>*:last-child]:pr-0"

/**
 * Bottom divider for an opaque sticky cell (pinned / selection / actions). These
 * cells sit at `z-index: 1` over the row, so they need to carry the row's bottom
 * divider themselves. A `box-shadow` (drawn 1px *outside* the box) is used rather
 * than `border-b` (drawn 1px *inside*, at the content-box edge): the row's own
 * `border-b` sits just below the content box, so a cell `border-b` lands 1px above
 * it and shows as a double line, while the shadow lands exactly on it. It also
 * survives the GPU-composited clip that erases a nested-sticky header cell's
 * bottom border (the header rowgroup is sticky and pinned cells are sticky within
 * it), which a `border-b` does not.
 */
export const STICKY_CELL_DIVIDER = "shadow-[0_1px_0_0_var(--coval-border)]"

/**
 * Focus ring for a focusable row. The row's own outline paints beneath its
 * opaque sticky cells (pinned / selection / actions, at `z-index: 1`), which
 * cut the ring off at both ends. This draws the ring in an overlay above them
 * instead, in the same place the global inset ring for rows sits: 1px inside a
 * 1px gap from the row's edge. The row is already positioned, so the overlay
 * resolves against it.
 */
export const ROW_FOCUS_RING_CLASS =
  "coval-custom-focus-ring focus-visible:outline-none focus-visible:after:pointer-events-none focus-visible:after:absolute focus-visible:after:inset-px focus-visible:after:z-[2] focus-visible:after:ring-1 focus-visible:after:ring-inset focus-visible:after:ring-coval-focus-ring focus-visible:after:content-['']"

/** Shared empty selection so the `selectedIds`-omitted case keeps a stable ref. */
export const EMPTY_SELECTION: ReadonlySet<string> = new Set()

/** Font conventions, keyed by column variant. */
export const VARIANT_CLASSES = {
  id: "font-mono text-coval-text-muted",
  name: "font-semibold text-coval-text",
  default: "text-coval-text",
} as const

export function variantClasses(variant: ColumnDef<unknown>["variant"]): string {
  return VARIANT_CLASSES[variant ?? "default"]
}

export type ColumnAlign = "left" | "right" | "center"

/**
 * Resolve a column's horizontal alignment. ID columns are right-aligned by
 * default; an explicit `align` still wins for any column.
 */
export function columnAlign<T>(col: ColumnDef<T> | undefined): ColumnAlign {
  if (col?.align) {
    return col.align
  }
  return col?.variant === "id" ? "right" : "left"
}

/** Flex/text classes for a resolved column alignment (`left` needs none). */
export function alignClasses(align: ColumnAlign): string {
  if (align === "right") {
    return "justify-end text-right"
  }
  if (align === "center") {
    return "justify-center text-center"
  }
  return ""
}

/**
 * Interactive elements a row may contain (CopyableId, links, future row
 * actions). Clicks / activations originating inside one of these should drive
 * that control, not row navigation.
 */
export const INTERACTIVE_DESCENDANT_SELECTOR =
  'a, button, input, select, textarea, [role="button"], [role="link"], [role="menuitem"], [role="checkbox"], [data-prevent-row-click="true"]'

/**
 * True unless the event started inside an interactive descendant of the row.
 * Checks `Element` (not `HTMLElement`) so clicks landing on an `svg`/`path`
 * inside an icon-only button or link are still attributed to that control.
 */
export function shouldNavigateRow(target: EventTarget | null): boolean {
  return !(
    target instanceof Element && target.closest(INTERACTIVE_DESCENDANT_SELECTOR)
  )
}

/**
 * True for a click that asks a link for a new tab: ⌘/Ctrl-click, or a
 * middle-click (which arrives as `auxclick`).
 */
export function isNewTabClick(event: React.MouseEvent): boolean {
  return event.button === 1 || event.metaKey || event.ctrlKey
}

/**
 * Opens a row's own path in a new tab, detached from this one, and reports
 * whether a tab opened. The `noopener` feature would make `window.open` return
 * `null` even on success, hiding a blocked popup, so the opener is severed on
 * the returned window instead.
 */
export function openRowInNewTab(href: string): boolean {
  const opened = window.open(href, "_blank")
  if (!opened) {
    return false
  }
  opened.opener = null
  return true
}

/**
 * True when a right-click inside a row belongs to the row's actions menu rather
 * than to the browser's own. An interactive control keeps its native menu, and
 * so does a selection the click lands inside, where the browser offers Copy.
 *
 * Checked on the way down, because the row's context-menu trigger is merged onto
 * the row element itself: a handler there runs alongside the trigger instead of
 * instead of it, so the event has to be stopped before it arrives.
 */
export function shouldOpenRowContextMenu(target: EventTarget | null): boolean {
  if (!shouldNavigateRow(target)) {
    return false
  }
  if (!(target instanceof Element)) {
    return true
  }
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || !selection.anchorNode) {
    return true
  }
  const row = target.closest('[role="row"]')
  return !row?.contains(selection.anchorNode)
}

/**
 * Resolves a row's copy group. A path is made absolute against the current
 * origin, so what lands on the clipboard is shareable rather than relative to
 * wherever it was copied from.
 */
export function resolveRowCopyTargets<T>({
  row,
  rowId,
  rowCopyId,
  rowHref,
}: {
  row: T
  rowId: string
  rowCopyId: boolean | ((row: T) => string | undefined) | undefined
  rowHref: ((row: T) => string | undefined) | undefined
}): RowCopyTargets {
  const id =
    rowCopyId === true
      ? rowId
      : typeof rowCopyId === "function"
        ? rowCopyId(row)
        : undefined
  const href = rowHref?.(row)
  return normalizeResourceCopyTargets({ id, url: href })
}

/** Stringify primitive cell values; objects/nullish render empty. */
export function renderFallback(value: unknown): React.ReactNode {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value)
  }
  return ""
}

/**
 * Default renderer for `id` columns: the standardized `CopyableId`
 * cell — 4-char display, copy icon on hover, click copies the full id. A string
 * value is required; anything else falls back to plain rendering.
 */
export function renderId(value: unknown): React.ReactNode {
  if (typeof value === "string") {
    return <CopyableId id={value} />
  }
  return renderFallback(value)
}

/**
 * Sticky-positioning facts for a pinned column, keyed by column id.
 * `offset` is the px distance from the pinned edge (it accumulates the widths
 * of the columns nearer that edge, plus the selection column on the left).
 * `edge` marks the column at the inner boundary of its group — it carries the
 * divider that separates the pinned block from the scrolling center. `outer`
 * marks the column at the outer (screen-edge) boundary of its group — it paints
 * the gutter cover (see `gutterCoverClasses`). A selection / actions column,
 * when present, is the outermost sticky cell, so the adjacent pinned column is
 * not `outer`.
 */
export interface PinMeta {
  side: "left" | "right"
  offset: number
  edge: boolean
  outer: boolean
  /** Right-pinned cells only: the grid line (counted from the end) the cell ends on. */
  gridColumnEnd?: number
}

/** Inline sticky offset for a pinned cell; `undefined` leaves the cell in flow. */
export function pinnedCellStyle(
  pin: PinMeta | undefined,
): React.CSSProperties | undefined {
  if (!pin) {
    return undefined
  }
  return pin.side === "left"
    ? { position: "sticky", left: pin.offset, zIndex: 1 }
    : {
        position: "sticky",
        right: pin.offset,
        zIndex: 1,
        gridColumnEnd: pin.gridColumnEnd,
      }
}

/**
 * Background + divider classes for a pinned cell. `self-stretch` makes the cell
 * fill the full row height (the row centers its cells, so without it the cell
 * sizes to its content) — this keeps the opaque background covering the whole
 * row so scrolling center cells can't bleed through, and makes the inner-edge
 * divider a single contiguous line down the column. The background tracks the
 * row's hover / selected state.
 */
/**
 * Background a highlighted row and its opaque cells take. A selected row gets
 * the blue selection wash; a row that is merely active — the keyboard cursor —
 * keeps the neutral step it has always had, so moving the cursor never reads as
 * picking a row. Platter rows step to `surface-default-hover` rather than to
 * the contrast surface they already sit on. Written out per branch so Tailwind
 * sees the literal class names.
 */
export function rowHighlightClass({
  selected,
  active,
  cardRows,
}: {
  selected?: boolean
  active?: boolean
  cardRows?: boolean
}): string | undefined {
  if (selected) {
    return "bg-surface-selected"
  }
  if (active) {
    return cardRows ? "bg-surface-default-hover" : "bg-surface-contrast"
  }
  return undefined
}

export function pinnedCellClasses(
  pin: PinMeta,
  opts: {
    selected?: boolean
    /** Keyboard/collection cursor, which highlights without being a pick. */
    active?: boolean
    warning?: boolean
    /** Whether this cell tints when the row is hovered (see `rowHover`). */
    hoverable?: boolean
    scrolledX?: boolean
    canScrollRight?: boolean
    cardRows?: boolean
    // A platter table's top header row, which floats on the contrast surface
    // outside the platters rather than sitting on one. Its pinned cells take
    // that surface and drop the divider, like the header row itself — painting
    // the default surface there leaves a lit block behind the pinned headers.
    platterHeader?: boolean
  } = {},
): string {
  // Draw the divider whenever center content actually sits under this edge:
  // under a left edge once scrolled from the start, under a right edge whenever
  // content is still hidden past the right (true at rest for an overflowing
  // table).
  const contentUnderEdge =
    pin.side === "left" ? opts.scrolledX : opts.canScrollRight
  return cn(
    opts.platterHeader
      ? "self-stretch bg-surface-contrast"
      : // The opaque cell carries the row's bottom divider (see
        // STICKY_CELL_DIVIDER).
        cn("self-stretch bg-surface-default", STICKY_CELL_DIVIDER),
    // An opaque pinned cell has to track the row it is part of, or it reads as
    // a differently-colored block while the row is highlighted.
    rowHighlightClass(opts),
    opts.warning && "bg-coval-warning-surface text-coval-warning-on-surface",
    // Hover matches the row's (see `BodyRow`).
    opts.hoverable && "group-hover:bg-surface-contrast",
    opts.warning && opts.hoverable && "group-hover:bg-coval-warning-surface",
    // A vertical line marks the pinned column's inner edge.
    pin.edge && contentUnderEdge && edgeDividerClasses(pin.side),
    pin.outer && gutterCoverClasses(pin.side, { divider: !opts.platterHeader }),
  )
}

/**
 * Marks a pinned column's inner edge with a 1px vertical line in the border
 * color, drawn just outside that edge so it sits over the scrolling center
 * content rather than inside the pinned cell's padding. Uses `before:` so it can
 * coexist with the outermost cell's `after:` gutter cover on a single pinned
 * column that is both outer and edge.
 */
export function edgeDividerClasses(side: "left" | "right"): string {
  return cn(
    "relative before:pointer-events-none before:absolute before:top-0 before:bottom-0 before:w-px before:bg-coval-border before:content-['']",
    side === "left" ? "before:left-full" : "before:right-full",
  )
}

/**
 * Classes that paint the `EDGE_GUTTER_PX` strip between an outermost sticky cell
 * and the scroll-container edge. Pinned / selection / action cells sit one
 * gutter in from that edge so their content lines up with the center columns;
 * without this, the strip would expose scrolling center cells during
 * horizontal scroll (content visible past a pinned column). A `bg-inherit`
 * pseudo-element fills the strip with the cell's own background, so it tracks
 * the row's hover / selected state and nothing shows through. `w-3` mirrors
 * `EDGE_GUTTER_PX` (12px), the same pairing as `EDGE_GUTTER_CLASS`.
 *
 * It also carries `border-b` so the row/header divider extends across the strip
 * (the cell's own `border-b` stops at the cell edge, and the opaque strip would
 * otherwise hide the container border under it at fractional DPR — see
 * `pinnedCellClasses`). `-bottom-px` drops the strip 1px past the padding box so
 * its border lands on the same row as the cell's border (which sits just below
 * the padding box), keeping the line continuous and 1px thick. `divider: false`
 * drops that border for a row that has none to extend — a platter table's
 * floating header, where it would otherwise read as a stray gutter-width rule
 * beside an undivided header.
 *
 * The strip stacks beneath the cell's own content (`after:-z-10`, inside the
 * stacking context the sticky cell's `z-index` makes) and still above the
 * scrolling cells it hides. A focus ring drawn past the cell's edge — by the
 * selection checkbox or the row-actions trigger, which sit flush on it — paints
 * over the strip rather than under it.
 *
 * Width is an explicit `after:w-[12px]` (not `after:w-3`) to exactly span
 * `EDGE_GUTTER_PX`: under the app's `html { font-size: 0.9rem }`, `w-3`
 * (0.75rem ≈ 10.8px) is ~1px narrower than the 12px sticky offset, leaving a
 * sliver where scrolling content peeks through past the pinned cell.
 */
export function gutterCoverClasses(
  side: "left" | "right",
  { divider = true }: { divider?: boolean } = {},
): string {
  return cn(
    "relative after:pointer-events-none after:absolute after:top-0 after:-bottom-px after:-z-10 after:w-[var(--table-edge-gutter,12px)] after:bg-inherit after:content-['']",
    divider && "after:border-b after:border-coval-border",
    side === "left" ? "after:right-full" : "after:left-full",
  )
}

/**
 * Reconcile a stored column order against the current default order: keep the
 * stored positions for columns that still exist, drop ids that no longer do,
 * and splice newly-added ids in at their default index. Keeps the uncontrolled
 * order stable across reorders while staying in sync when `columns` changes.
 */
export function reconcileColumnOrder(
  stored: string[],
  fallback: string[],
): string[] {
  const fallbackSet = new Set(fallback)
  const kept = stored.filter((id) => fallbackSet.has(id))
  // Every still-valid id is present (kept ⊆ fallback, equal length) — keep the
  // stored order as-is; nothing was added.
  if (kept.length === fallback.length) {
    return kept
  }
  const result = [...kept]
  fallback.forEach((id, index) => {
    if (!result.includes(id)) {
      result.splice(Math.min(index, result.length), 0, id)
    }
  })
  return result
}

// Bridge the consumer-facing expanded-row-id array and TanStack's ExpandedState
// record (`{ [rowId]: true }`), so expansion mirrors the `columnOrder` /
// `selectedIds` id-array conventions rather than leaking the internal shape.
export function expandedIdsToRecord(
  ids: string[] | undefined,
): Record<string, boolean> {
  const record: Record<string, boolean> = {}
  for (const id of ids ?? []) {
    record[id] = true
  }
  return record
}

export function recordToExpandedIds(record: Record<string, boolean>): string[] {
  return Object.keys(record).filter((id) => record[id])
}
